/**
 * Typed accessors over the baked dataset — THE IDENTITY HALF.
 *
 * The JSON is generated by `npm run bake` and committed. Nothing in this
 * package touches the network at runtime (spec section 6).
 *
 * ===========================================================================
 * THIS ENTRY POINT DOES NOT CARRY THE ANSWER KEY (invariant 5)
 * ===========================================================================
 *
 * `identity.json` is `countries.json` with every answer-bearing field removed:
 * no capital, no population, no borders, no languages, no currencies, no UN
 * accession year. It is what the globe, the reveal cards and the results
 * screen actually need, and it is what the browser bundle should carry.
 *
 * The full record lives behind `@geogame/data/facts` and is imported by the
 * server, by `@geogame/rules`, and by the client's lazily-loaded practice
 * driver — and by nothing that a ranked player's browser loads eagerly. When
 * the two were one module, a single regex over the shipped entry chunk
 * recovered 198 complete country records including `unJoinedYear`, which is
 * the answer key for the x9 golden rung.
 *
 * The split is enforced three ways: by the TYPE (`CountryIdentity` has no
 * `population` to access), by `packages/data/src/dataset.test.ts` (the two
 * generated files must agree on identity and disagree on nothing else), and by
 * `scripts/e2e.ts`, which greps the BUILT bundle — the artefact an attacker
 * actually reads.
 */

import type { Feature, FeatureCollection, Geometry } from 'geojson';
import type { Tier } from '@geogame/shared';
import { TIER_CONFIG } from '@geogame/shared';

import identityJson from './generated/identity.json' with { type: 'json' };
import geojsonJson from './generated/countries-110m.json' with { type: 'json' };
import microshapesJson from './generated/microshapes.json' with { type: 'json' };
import metaJson from './generated/meta.json' with { type: 'json' };

import type {
  CountryFeatureProperties,
  CountryIdentity,
  CurrencyRecord,
  DatasetMeta,
  FlagFamily,
  MicroshapeProperties,
} from './types';

export type {
  CountryIdentity,
  CountryFeatureProperties,
  CurrencyRecord,
  DatasetMeta,
  FlagFamily,
  MicroshapeProperties,
};
export { FLAG_FAMILIES } from './types';

export type CountryFeature = Feature<Geometry, CountryFeatureProperties>;
export type CountryFeatureCollection = FeatureCollection<Geometry, CountryFeatureProperties>;

export type MicroshapeFeature = Feature<Geometry, MicroshapeProperties>;
export type MicroshapeCollection = FeatureCollection<Geometry, MicroshapeProperties>;

// The generated modules are typed as `unknown` on purpose — see
// src/generated/generated.d.ts. These three casts are the only place the shape
// is asserted, and the bake is what guarantees it.
const COUNTRIES = identityJson as unknown as CountryIdentity[];
const GEOJSON = geojsonJson as unknown as CountryFeatureCollection;
const MICROSHAPES = microshapesJson as unknown as MicroshapeCollection;
const META = metaJson as unknown as DatasetMeta;

const INDEX: ReadonlyMap<string, CountryIdentity> = new Map(
  COUNTRIES.map((c) => [c.cca3, c] as const),
);

const FEATURE_INDEX: ReadonlyMap<string, CountryFeature> = new Map(
  GEOJSON.features.map((f) => [f.properties.cca3, f] as const),
);

const POOL_CACHE = new Map<Tier, readonly CountryIdentity[]>();

/** Every baked country, ordered by fameRank ascending. Includes disputed. */
export function allCountries(): readonly CountryIdentity[] {
  return COUNTRIES;
}

/** Look up one country by ISO alpha-3. */
export function byCca3(cca3: string): CountryIdentity | undefined {
  return INDEX.get(cca3.toUpperCase());
}

/** The whole alpha-3 index, for callers that need repeated lookups. */
export function countryIndex(): ReadonlyMap<string, CountryIdentity> {
  return INDEX;
}

/**
 * The scored country pool for a tier: fame-ordered, disputed territories
 * removed (spec section 6), microstates filtered per tier config, truncated to
 * the tier's pool size.
 *
 * `@geogame/data/facts` re-implements this over the FULL records rather than
 * widening these. Two small functions beat one that has to be told which half
 * of the dataset the caller is allowed to see.
 */
export function poolForTier(tier: Tier): readonly CountryIdentity[] {
  const cached = POOL_CACHE.get(tier);
  if (cached) return cached;

  const cfg = TIER_CONFIG[tier];
  const pool = COUNTRIES.filter(
    (c) =>
      !c.isDisputed &&
      c.tiers.includes(tier) &&
      (cfg.allowMicrostates || !c.isMicrostate),
  ).slice(0, cfg.countryPoolSize);

  POOL_CACHE.set(tier, pool);
  return pool;
}

/** Label-free country polygons, each carrying a `cca3` property (invariant 7). */
export function geojson(): CountryFeatureCollection {
  return GEOJSON;
}

/** One country's polygon, or undefined when the 110m set has no geometry for it. */
export function featureFor(cca3: string): CountryFeature | undefined {
  return FEATURE_INDEX.get(cca3.toUpperCase());
}

/**
 * Drawable geometry for the countries `countries-110m.json` has none of.
 *
 * DRAWING ONLY. Nothing hit-tests this: `geojson()` is still the whole of what
 * the globe queries, `countriesWithoutGeometry()` still names exactly the same
 * countries, and the point-proximity path that makes them answerable is
 * untouched. It exists so those countries can be DRAWN as the islands they are
 * instead of as a circle. See `buildMicroshapes` in `scripts/bake.ts`.
 *
 * One country is missing from it: Kosovo has no ISO numeric code, so no
 * Natural Earth feature matches it at any resolution.
 */
export function microShapes(): MicroshapeCollection {
  return MICROSHAPES;
}

/** Bake report: counts and any unmatched geometry ids. */
export function datasetMeta(): DatasetMeta {
  return META;
}

/**
 * Countries the 110m polygon set has no geometry for — almost all microstates
 * and small island states. They are sub-pixel at world zoom and Natural Earth
 * 110m simply drops them, so the globe CANNOT hit-test them by polygon. They
 * are pinpointable only via centroid proximity inside the tier's snap radius
 * (spec section 6). Any globe implementation must handle this list or those
 * countries are unaskable.
 */
export function countriesWithoutGeometry(): readonly string[] {
  return META.countriesWithoutGeometry;
}

export type { CityRecord } from './cities';
export { allCities, cityPoolForTier } from './cities';
